import { useState } from "react";

type Props = {
  address: string;
  avatarUrl?: string | null;
  name?: string | null;
  size?: number;
  className?: string;
};

// Deterministic pleasant colour from the address, used for the initials fallback.
function hueFor(address: string): number {
  return parseInt(address.slice(2, 8) || "0", 16) % 360;
}

/**
 * Circular trader avatar: shows the uploaded profile photo when present, and
 * otherwise a coloured monogram derived from the display name or address. Falls
 * back to the monogram if the image fails to load.
 */
export function TraderAvatar({ address, avatarUrl, name, size = 32, className = "" }: Props) {
  const [errored, setErrored] = useState(false);
  const dim = { width: size, height: size };

  if (avatarUrl && !errored) {
    return (
      <img
        className={`CT-avatar ${className}`}
        style={dim}
        src={avatarUrl}
        alt={name || address}
        loading="lazy"
        onError={() => setErrored(true)}
      />
    );
  }

  const initials = (name ? name.replace(/[^a-zA-Z0-9]/g, "").slice(0, 2) : address.slice(2, 4)).toUpperCase();
  return (
    <div
      className={`CT-avatar CT-avatar--fallback ${className}`}
      style={{ ...dim, background: `hsl(${hueFor(address)} 55% 45%)`, fontSize: Math.round(size * 0.4) }}
      aria-label={name || address}
    >
      {initials}
    </div>
  );
}
